import { api, publicApi } from "@/lib/axios";
import {
  Client,
  ClientCreateRequest,
  ClientUpdateRequest,
} from "@/types/clients";
import {
  AcceptedQuote,
  PublicQuote,
  Quote,
  QuoteAcceptedResponse,
  QuoteAcceptRequest,
  QuoteCreateRequest,
  QuoteWSUpdate,
} from "@/types/quotes";
import { CreateTradeResponse, Trade, TradeCreateRequest } from "@/types/trades";
import {
  Asset,
  AssetBase,
  AssetUpdate,
  AssetType,
} from "@/types/assets";
import {
  User,
  UserCreateRequest,
  UserUpdateRequest,
  PasswordChangeRequest,
  AdminPasswordChangeRequest,
} from "@/types/auth";
import {
  VirtualMarketCreateSchema,
  VirtualMarketResponseSchema,
  VirtualMarketUpdateSchema,
} from "@/types/vmarkets";

// Authentication API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await publicApi.post("/auth/login", { email, password });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get("/auth/me");
    return response.data;
  },
  logout: () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  },
};

// Quotes API
export const quotesAPI = {
  getQuotes: async (params?: {
    page?: number;
    size?: number;
    vmarket_ids?: number[];
    status?: string[];
    start_date?: string;
    end_date?: string;
  }): Promise<{
    items: Quote[];
    total: number;
    page: number;
    size: number;
    pages: number;
    filters?: {
      vmarket_ids?: number[];
      status?: string[];
      start_date?: string;
      end_date?: string;
    };
  }> => {
    const response = await api.get<{
      items: Quote[];
      total: number;
      page: number;
      size: number;
      pages: number;
      filters?: {
        vmarket_id?: number;
        status?: string[];
        start_date?: string;
        end_date?: string;
      };
    }>("/quotes", { params });
    return response.data;
  },

  getQuoteBySlug: async (slug: string): Promise<Quote> => {
    const response = await api.get<Quote>(`/quotes/${slug}`);
    return response.data;
  },

  createQuote: async (quoteData: QuoteCreateRequest): Promise<Quote> => {
    const response = await api.post<Quote>("/quotes/create", quoteData);
    return response.data;
  },

  getAcceptedQuotes: async (params?: {
    page?: number;
    size?: number;
    status?: string[];
    vmarket_ids?: number[];
    start_date?: string;
    end_date?: string;
  }): Promise<{
    items: AcceptedQuote[];
    total: number;
    page: number;
    size: number;
    pages: number;
    filters?: {
      vmarket_ids?: number[];
      status?: string[];
      start_date?: string;
      end_date?: string;
    };
  }> => {
    const response = await api.get<{
      items: AcceptedQuote[];
      total: number;
      page: number;
      size: number;
      pages: number;
      filters?: {
        vmarket_id?: number;
        status?: string[];
        start_date?: string;
        end_date?: string;
      };
    }>("/accepted-quotes", { params });
    return response.data;
  },

  cancelQuote: async (slug: string): Promise<Quote> => {
    const response = await api.post<Quote>(`/quotes/${slug}/cancel`);
    return response.data;
  },

  cancelAcceptedQuote: async (slug: string): Promise<AcceptedQuote> => {
    const response = await api.post<AcceptedQuote>(`/accepted-quotes/${slug}/cancel`);
    return response.data;
  },

  exportQuotesCSV: async () => {
    const response = await api.get("/quotes/export", {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "quotes_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },
};

// Public Quotes API
export const publicQuotesAPI = {
  getQuoteBySlug: async (slug: string): Promise<PublicQuote> => {
    const response = await publicApi.get<PublicQuote>(`/public/quotes/${slug}`);
    return response.data;
  },

  refreshQuote: async (slug: string): Promise<QuoteWSUpdate> => {
    const response = await publicApi.get<QuoteWSUpdate>(
      `/public/quotes/${slug}/refresh`
    );
    return response.data;
  },

  acceptQuote: async (
    slug: string,
    acceptData: QuoteAcceptRequest
  ): Promise<QuoteAcceptedResponse> => {
    const response = await publicApi.post<QuoteAcceptedResponse>(
      `/public/quotes/${slug}/accept`,
      acceptData
    );
    return response.data;
  },

  addAcceptedQuoteCryptoAddress: async (
    slug: string,
    crypto_address: string
  ): Promise<AcceptedQuote> => {
    const response = await publicApi.post<AcceptedQuote>(
      `/public/quotes/accepted/${slug}/crypto-address`,
      { crypto_address }
    );
    return response.data;
  },
};

// Clients API
export const clientsAPI = {
  getClients: async (list_not_enabled: boolean = false) => {
    const response = await api.get<Client[]>("/clients", {
      params: { list_not_enabled },
    });
    return response.data;
  },

  getClient: async (id: string) => {
    const response = await api.get<Client>(`/clients/${id}`);
    return response.data;
  },

  createClient: async (client: ClientCreateRequest) => {
    const response = await api.post<Client>("/clients", client);
    return response.data;
  },

  updateClient: async (id: string, client: ClientUpdateRequest) => {
    const response = await api.put<Client>(`/clients/${id}`, client);
    return response.data;
  },

  deleteClient: async (id: string) => {
    const response = await api.delete<Client>(`/clients/${id}`);
    return response.data;
  },

  purgeTestData: async () => {
    const response = await api.delete("/clients/test/purge");
    return response.data;
  },
};

// Trades API
export const tradesAPI = {
  getTrades: async (params?: {
    page?: number;
    size?: number;
    vmarket_ids?: number[];
    start_date?: string;
    end_date?: string;
  }): Promise<{
    items: Trade[];
    total: number;
    page: number;
    size: number;
    pages: number;
    filters?: {
      vmarket_ids?: number[];
      start_date?: string;
      end_date?: string;
    };
  }> => {
    const response = await api.get<{
      items: Trade[];
      total: number;
      page: number;
      size: number;
      pages: number;
      filters?: {
        vmarket_id?: number;
        start_date?: string;
        end_date?: string;
      };
    }>("/trades", { params });
    return response.data;
  },

  createTrade: async (tradeData: TradeCreateRequest): Promise<CreateTradeResponse> => {
    const response = await api.post<CreateTradeResponse>("/trades/create", tradeData);
    return response.data;
  },

  getTradeById: async (id: number): Promise<Trade> => {
    const response = await api.get<Trade>(`/trades/${id}`);
    return response.data;
  },

  updateTradeExecutionPrice: async (tradeId: number, execution_price: string | number): Promise<Trade> => {
    const response = await api.patch<Trade>(`/trades/${tradeId}/update-execution-price`, { execution_price: Number(execution_price) });
    return response.data;
  },
};

// Assets API
interface AssetParams {
  asset_type?: AssetType;
  list_not_enabled?: boolean;
}

export const assetsAPI = {
  getAssets: async (params: AssetParams = {}) => {
    const response = await api.get<Asset[]>("/assets", {
      params,
    });
    return response.data;
  },

  getAsset: async (id: number) => {
    const response = await publicApi.get<Asset>(`/assets/${id}`);
    return response.data;
  },

  createAsset: async (asset: AssetBase) => {
    const response = await api.post<Asset>("/assets", asset);
    return response.data;
  },

  updateAsset: async (id: number, asset: AssetUpdate) => {
    const response = await api.put<Asset>(`/assets/${id}`, asset);
    return response.data;
  },

  deleteAsset: async (id: number) => {
    const response = await api.delete<Asset>(`/assets/${id}`);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (list_not_enabled: boolean = false) => {
    const response = await api.get<User[]>("/users", {
      params: { list_not_enabled },
    });
    return response.data;
  },

  getUser: async (id: string) => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  createUser: async (user: UserCreateRequest) => {
    const response = await api.post<User>("/users", user);
    return response.data;
  },

  updateUser: async (id: string, user: UserUpdateRequest) => {
    const response = await api.put<User>(`/users/${id}`, user);
    return response.data;
  },

  deleteUser: async (id: string) => {
    const response = await api.delete<User>(`/users/${id}`);
    return response.data;
  },

  changePassword: async (data: PasswordChangeRequest) => {
    const response = await api.post("/auth/change-password", data);
    return response.data;
  },

  changeUserPassword: async (id: string, data: AdminPasswordChangeRequest) => {
    const response = await api.post(`/users/${id}/change-password`, data);
    return response.data;
  },
};

// Virtual Markets API
export const vmarketsAPI = {
  getVirtualMarkets: async (list_not_enabled: boolean = false) => {
    const response = await api.get<VirtualMarketResponseSchema[]>("/vmarkets", {
      params: { list_not_enabled },
    });
    return response.data;
  },

  getVirtualMarket: async (id: number) => {
    const response = await api.get<VirtualMarketResponseSchema>(`/vmarkets/${id}`);
    return response.data;
  },

  createVirtualMarket: async (vmarket: VirtualMarketCreateSchema) => {
    const response = await api.post<VirtualMarketResponseSchema>("/vmarkets", vmarket);
    return response.data;
  },

  updateVirtualMarket: async (id: number, vmarket: VirtualMarketUpdateSchema) => {
    const response = await api.put<VirtualMarketResponseSchema>(`/vmarkets/${id}`, vmarket);
    return response.data;
  },

  deleteVirtualMarket: async (id: number) => {
    const response = await api.delete<{
      event: string;
      vmarket_id: number;
      is_enabled: boolean;
      is_deleted: boolean;
      deleted_at: string;
    }>(`/vmarkets/${id}`);
    return response.data;
  },
};

// Export API instance
export default api;
